import EventEmitter from 'node:events';

/**
 * Application-wide events
 */
export enum AppEvent {
  // Gamification events
  BADGE_UNLOCKED = 'badge.unlocked',
  LEVEL_UP = 'user.levelUp',
  QUEST_COMPLETED = 'quest.completed',
  ACHIEVEMENT_EARNED = 'achievement.earned',
  
  // Collaboration events
  PLAN_SHARED = 'plan.shared',
  PLAN_SHARE_REMOVED = 'plan.shareRemoved',
  COLLABORATOR_ADDED = 'plan.collaboratorAdded',
  COMMENT_ADDED = 'plan.commentAdded',
  
  // Planner events
  PLAN_CREATED = 'plan.created',
  PLAN_DELETED = 'plan.deleted',
  STAGE_ADDED = 'plan.stageAdded',
  STAGE_REMOVED = 'plan.stageRemoved',
  
  // AI events
  AI_SUGGESTION_USED = 'ai.suggestionUsed',
}

/**
 * Event payload types
 */
export interface BadgeUnlockedPayload {
  userId: string;
  badgeId: string;
  badgeName: string;
  timestamp: number;
}

export interface LevelUpPayload {
  userId: string;
  newLevel: number;
  timestamp: number;
}

export interface PlanSharedPayload {
  planId: string;
  ownerId: string;
  recipientId: string;
  shareType: 'view' | 'edit';
  timestamp: number;
}

export interface PlanShareRemovedPayload {
  planId: string;
  ownerId: string;
  shareId: string;
  timestamp: number;
}

export interface StageRemovedPayload {
  planId: string;
  stageId: string;
  userId: string;
  timestamp: number;
}

export interface CollaboratorAddedPayload {
  planId: string;
  planName: string;
  addedById: string;
  collaboratorId: string;
  timestamp: number;
}

export interface CommentAddedPayload {
  planId: string;
  planName: string;
  commentId: string;
  authorId: string;
  text: string;
  timestamp: number;
}

export type EventPayload = 
  | BadgeUnlockedPayload 
  | LevelUpPayload 
  | PlanSharedPayload 
  | PlanShareRemovedPayload
  | StageRemovedPayload
  | CollaboratorAddedPayload
  | CommentAddedPayload;

/**
 * Global application event emitter
 */
class AppEventEmitter extends EventEmitter {
  constructor() {
    super();
    this.setMaxListeners(20); // Support multiple listeners per event
  }

  /**
   * Emit typed event with payload
   */
  emitEvent(event: AppEvent, payload: EventPayload): void {
    this.emit(event, payload);
  }

  /**
   * Subscribe to typed event
   */
  onEvent(event: AppEvent, handler: (payload: EventPayload) => void | Promise<void>): void {
    this.on(event, handler);
  }

  /**
   * Unsubscribe from event
   */
  offEvent(event: AppEvent, handler: (payload: EventPayload) => void | Promise<void>): void {
    this.off(event, handler);
  }
}

// Singleton instance
export const appEvents = new AppEventEmitter();

/**
 * Helper functions for common events
 */
export const emitBadgeUnlocked = (payload: BadgeUnlockedPayload) => {
  appEvents.emitEvent(AppEvent.BADGE_UNLOCKED, payload);
};

export const emitLevelUp = (payload: LevelUpPayload) => {
  appEvents.emitEvent(AppEvent.LEVEL_UP, payload);
};

export const emitPlanShared = (payload: PlanSharedPayload) => {
  appEvents.emitEvent(AppEvent.PLAN_SHARED, payload);
};

export const emitPlanShareRemoved = (payload: PlanShareRemovedPayload) => {
  appEvents.emitEvent(AppEvent.PLAN_SHARE_REMOVED, payload);
};

export const emitCollaboratorAdded = (payload: CollaboratorAddedPayload) => {
  appEvents.emitEvent(AppEvent.COLLABORATOR_ADDED, payload);
};

export const emitStageRemoved = (payload: StageRemovedPayload) => {
  appEvents.emitEvent(AppEvent.STAGE_REMOVED, payload);
};

export const emitCommentAdded = (payload: CommentAddedPayload) => {
  appEvents.emitEvent(AppEvent.COMMENT_ADDED, payload);
};
